import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { SortType } from "../../../../../../models/SortType";
import {
  Box,
  CircularProgress,
  Paper,
  Backdrop,
  Grid2,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import { useStores } from "../../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import SwageazyInventoryProductsTable from "../../../components/swageazy-inventory/SwageazyInventoryProductsTable";
import {
  SwageazyInventoryFilters,
  SWAGEAZY_INVENTORY_STOCK_STATUS_OPTIONS,
  parseQtyInput,
  resolveInventorySearchText,
} from "../../../../../../models/SwageazyInventory/SwageazyInventoryFilter";
import AppDropDown from "../../../../../../views/components/common/filter/AppDropDown";
import { SwageazyInventoryDetails } from "../../../../../../models/SwageazyInventory/SwageazyInventory";
import ImageGalleryPopup from "../../../../../../views/components/common/pop-up/ImageGalleryPopup";
import SwageazyInventoryInwardPopup from "./SwageazyInventoryInwardPopup";
import SwageazyInventoryOutwardPopup from "./SwageazyInventoryOutwardPopup";
import SwageazyInventorySampleReturn from "./SwageazyInventorySampleReturn";
import SwageazyInventoryBoxTransferPopup from "./SwageazyInventoryBoxTransferPopup";
import SwageazyInventoryRackTransferPopup from "./SwageazyInventoryRackTransferPopup";
import SwageazyInventoryStockQuantityDetailsPopup from "./SwageazyInventoryStockQyantityDetailsPopup";
import CreatePurchaseRequestPopup from "../sourcing-ops/CreatePrPopup";
import dayjs from "dayjs";
import {
  PurchaseRequestProductRequestBody,
  PurchaseRequestSkuVariantRequestBody,
} from "../../../../../../models/PurchaseRequest/PurchaseRequestProductRequestBody";
import {
  isValidPurchaseRequestSkuVariant,
  toPurchaseRequestSkuVariantRequestBody,
} from "../../../../../../utils/purchaseRequestSkuVariants";
import { useDebounce } from "../../../../../../views/components/common/hooks/Debounce";
import SwageazyInventoryAdjustmentStore from "../../../../stores/SwageazyInventoryAdjustmentStore";
import SwageazyInventoryAdjustPopup from "./adjustments/SwageazyInventoryAdjustPopup";
import SwageazyInventorySkuTransferPopup from "./adjustments/SwageazyInventorySkuTransferPopup";
import SwageazyInventoryAdjustCsvPopup, {
  downloadInventoryAdjustmentTemplate,
} from "./adjustments/SwageazyInventoryAdjustCsvPopup";
import SwageazyInventoryAuditLogDrawer from "./adjustments/SwageazyInventoryAuditLogDrawer";
import SwageazyInventoryTransactionsDrawer from "./adjustments/SwageazyInventoryTransactionsDrawer";

const SwageazyInventoryListing: React.FC = () => {
  const { userStore, swageazyInventoryStore, warehouseStore } = useStores();
  const theme = useTheme();
  const { showError, showSuccess } = useNotification();
  const [adjustmentStore] = useState(
    () => new SwageazyInventoryAdjustmentStore(),
  );
  const canEditInventory = userStore.hasPermission([
    EmployeeRoles.EDIT_SWAGEAZY_INVENTORY,
  ]);
  const canAdjustMoveInventory = userStore.hasPermission([
    EmployeeRoles.ADJUST_MOVE_SWAGEAZY_INVENTORY,
  ]);

  const canTransferSKU = userStore.hasPermission([EmployeeRoles.ALL]);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const parseUrlParams = (): SwageazyInventoryFilters => {
    const stockStatus = searchParams.get("stock_status") || "";
    const isValidStockStatus = SWAGEAZY_INVENTORY_STOCK_STATUS_OPTIONS.some(
      (option) => option.id === stockStatus,
    );
    const parseQty = (key: string): number | undefined => {
      const raw = searchParams.get(key);
      if (!raw || !/^\d+$/.test(raw)) return undefined;
      return Number(raw);
    };
    const qtyMin = parseQty("available_qty_min");
    const qtyMax = parseQty("available_qty_max");
    const hasInvalidQtyRange =
      qtyMin !== undefined && qtyMax !== undefined && qtyMin > qtyMax;

    return {
      page_no: parseInt(searchParams.get("page_no") || "1", 10),
      search_text: searchParams.get("search_text") || undefined,
      warehouse_id: searchParams.get("warehouse_id") || "",
      stock_status: isValidStockStatus
        ? (stockStatus as SwageazyInventoryFilters["stock_status"])
        : "",
      available_qty_min: hasInvalidQtyRange ? undefined : qtyMin,
      available_qty_max: hasInvalidQtyRange ? undefined : qtyMax,
      limit: parseInt(searchParams.get("limit") || "30", 10),
      sort_by: searchParams.get("sort_by") || "last_updated_at",
      sort_type:
        searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    };
  };

  const initialParams: SwageazyInventoryFilters = parseUrlParams();
  const [params, setParams] = useState<SwageazyInventoryFilters>(initialParams);
  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const skuCsvInputRef = useRef<HTMLInputElement | null>(null);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const [draftParams, setDraftParams] = useState<SwageazyInventoryFilters>({
    warehouse_id: initialParams.warehouse_id || "",
    stock_status: initialParams.stock_status || "",
    available_qty_min: initialParams.available_qty_min,
    available_qty_max: initialParams.available_qty_max,
  });
  const [qtyFilterError, setQtyFilterError] = useState("");

  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    const newSearch = resolveInventorySearchText(debouncedSearchTerm);

    setParams((prev: SwageazyInventoryFilters) => {
      const prevSearch = prev.search_text ?? "";
      const nextSearch = newSearch ?? "";

      if (prevSearch === nextSearch) return prev;

      return {
        ...prev,
        search_text: newSearch,
        page_no: 1,
      };
    });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (userStore.hasPermission([EmployeeRoles.VIEW_SWAGEAZY_INVENTORY])) {
      swageazyInventoryStore.isLoading = true;
      swageazyInventoryStore
        .getSwageazyInventoryListing(params)
        .then(() => {})
        .catch((err: any) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          );
        })
        .finally(() => {
          swageazyInventoryStore.isLoading = false;
          swageazyInventoryStore.isFetchingData = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_SWAGEAZY_INVENTORY]))
      return;
    if (warehouseStore.warehouses.length > 0) return;
    warehouseStore.searchWarehouse().catch(() => {
      showError("Error", "Failed to fetch warehouses.");
    });
  }, [userStore, warehouseStore, showError]);

  const applyDraftFilters = () => {
    const minQty = draftParams.available_qty_min;
    const maxQty = draftParams.available_qty_max;
    if (minQty !== undefined && maxQty !== undefined && minQty > maxQty) {
      const message = "Min available qty cannot be greater than max.";
      setQtyFilterError(message);
      showError("Invalid filter", message);
      return;
    }
    setQtyFilterError("");
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      warehouse_id: draftParams.warehouse_id || "",
      stock_status: draftParams.stock_status || "",
      available_qty_min: minQty,
      available_qty_max: maxQty,
      search_text: resolveInventorySearchText(searchTerm),
      page_no: 1,
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      applyDraftFilters();
    }
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
  };

  const resetFilters = () => {
    setSearchTerm("");
    setQtyFilterError("");
    setDraftParams({
      warehouse_id: "",
      stock_status: "",
      available_qty_min: undefined,
      available_qty_max: undefined,
    });
    setParams((prev: SwageazyInventoryFilters) => ({
      ...prev,
      search_text: undefined,
      warehouse_id: "",
      stock_status: "",
      available_qty_min: undefined,
      available_qty_max: undefined,
      page_no: 1,
    }));
  };

  const handleApply = () => {
    applyDraftFilters();
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          if (
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean"
          ) {
            queryParams.append(key, String(value));
          }
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        navigate({ search: `?${queryString}` }, { replace: true });
      } else {
        navigate({ search: "" }, { replace: true });
      }
    };
    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  const handleViewImagesOfProduct = (product: SwageazyInventoryDetails) => {
    swageazyInventoryStore.setSelectedProductImages(
      product?.id,
      product?.images || [],
    );
    swageazyInventoryStore.managePopup("openImageGallery", "open");
  };

  const handleViewInwardQuantityDetails = (id: number) => {
    swageazyInventoryStore.selectedProduct = id;
    swageazyInventoryStore.managePopup(
      "InventoryStockQuantityDetailsPopup",
      "open",
    );
    swageazyInventoryStore.getProductStockDetails(id).catch((err: any) => {
      showError(
        "Error",
        err?.response?.data?.message ||
          "Something went wrong! Please try again later.",
      );
    });
  };

  const handlePr = () => {
    if (swageazyInventoryStore.isLoading) {
      return;
    }

    swageazyInventoryStore.setIsLoading(true);

    const vendorId = Array.isArray(
      swageazyInventoryStore.purchaseRequestFormData?.vendor_id,
    )
      ? Number(
          swageazyInventoryStore.purchaseRequestFormData?.vendor_id?.[0]?.id,
        )
      : Number(swageazyInventoryStore.purchaseRequestFormData?.vendor_id);

    if (!vendorId) {
      swageazyInventoryStore.setIsLoading(false);
      showError("Error occurred", "Please select vendor");
      return;
    }

    if (
      vendorId === Number(process.env.REACT_APP_VENDOR_ID) &&
      !swageazyInventoryStore.purchaseRequestFormData?.warehouse_id
    ) {
      swageazyInventoryStore.setIsLoading(false);
      showError("Error occurred", "Please select city and warehouse");
      return;
    }

    if (
      !swageazyInventoryStore.purchaseRequestFormData?.expected_delivery_date
    ) {
      swageazyInventoryStore.setIsLoading(false);
      showError("Error occurred", "Please select expected delivery date");
      return;
    }

    const products: PurchaseRequestProductRequestBody[] = [];
    const requestSkuVariants: PurchaseRequestSkuVariantRequestBody[] = [];

    swageazyInventoryStore.purchaseRequestProducts?.forEach((prProduct) => {
      const product = swageazyInventoryStore.products?.find(
        (p) => p.id === prProduct.id,
      );
      if (!product) return;

      const productSkuVariants = (
        swageazyInventoryStore.selectedSkuVariants.get(prProduct.id || 0) || []
      ).filter(isValidPurchaseRequestSkuVariant);

      if (productSkuVariants.length === 0) return;

      const quantity = productSkuVariants.reduce(
        (total, variant) => total + (variant.quantity || 0),
        0,
      );
      const amount = productSkuVariants.reduce(
        (total, variant) =>
          total + (variant.quantity || 0) * (variant.cost_price || 0),
        0,
      );
      const averageCostPrice = quantity > 0 ? amount / quantity : 0;

      products.push({
        id: prProduct.id,
        quantity,
        rate: averageCostPrice,
        cost_price: averageCostPrice,
        sku: product.sku || undefined,
        description: prProduct.description || product.description || undefined,
        gst_percentage: product.gst_percentage || prProduct.gst_percentage,
      });

      requestSkuVariants.push(
        ...productSkuVariants.map((variant) =>
          toPurchaseRequestSkuVariantRequestBody(variant, prProduct.id!),
        ),
      );
    });

    // COMMENTED OUT: Old variant-based processing
    // swageazyInventoryStore.selectedProductVariants.forEach(
    //   (variants, productId) => {
    //     const product = swageazyInventoryStore.products?.find(
    //       (p) => p.id === productId
    //     );
    //     if (!product) return;

    //     variants?.forEach((variant) => {
    //       if (
    //         variant?.quantity &&
    //         variant?.costPrice &&
    //         variant?.quantity > 0 &&
    //         variant?.costPrice > 0
    //       ) {
    //         const prProduct =
    //           swageazyInventoryStore.purchaseRequestProducts?.find(
    //             (p) => p?.id === productId
    //           );

    //         const sizeValue = variant?.selectedOptions?.["1"] || "";
    //         const colorValue = variant?.selectedOptions?.["2"] || "";

    //         let sku = "";
    //         if (sizeValue && colorValue) {
    //           sku = `${sizeValue}-${colorValue}`.toLowerCase();
    //         } else if (sizeValue) {
    //           sku = sizeValue.toLowerCase();
    //         } else if (colorValue) {
    //           sku = colorValue.toLowerCase();
    //         }

    //         products.push({
    //           id: productId,
    //           quantity: variant?.quantity,
    //           rate: variant?.costPrice,
    //           sku: sku || undefined,
    //           description:
    //             prProduct?.description || product?.description || undefined,
    //         });
    //       }
    //     });
    //   }
    // );

    if (products.length === 0) {
      swageazyInventoryStore.setIsLoading(false);
      showError(
        "Error occurred",
        "Please add at least one product with quantity and cost price",
      );
      return;
    }

    const expectedDeliveryDate =
      swageazyInventoryStore.purchaseRequestFormData?.expected_delivery_date;
    const expectedDeliveryDateString =
      expectedDeliveryDate instanceof Date
        ? dayjs(expectedDeliveryDate).format("YYYY-MM-DD HH:mm:ss")
        : typeof expectedDeliveryDate === "string"
          ? dayjs(expectedDeliveryDate).format("YYYY-MM-DD HH:mm:ss")
          : dayjs().format("YYYY-MM-DD HH:mm:ss");

    swageazyInventoryStore.setPurchaseRequestFormData({
      emails: swageazyInventoryStore.purchaseRequestFormData?.emails || [],
      vendor_id: vendorId,
      vendor_name: swageazyInventoryStore.purchaseRequestFormData?.vendor_name,
      warehouse_id:
        swageazyInventoryStore.purchaseRequestFormData?.warehouse_id,
      warehouse_address:
        swageazyInventoryStore.purchaseRequestFormData?.warehouse_address,
      expected_delivery_date: expectedDeliveryDateString,
      is_urgent_sourcing_request:
        swageazyInventoryStore.purchaseRequestFormData
          ?.is_urgent_sourcing_request || false,
      urgent_payment_percentage: swageazyInventoryStore.purchaseRequestFormData
        ?.is_urgent_sourcing_request
        ? (swageazyInventoryStore.purchaseRequestFormData
            .urgent_payment_percentage ?? null)
        : null,
      pr_products: products,
      sku_variants: requestSkuVariants,
      vendor_email:
        swageazyInventoryStore.purchaseRequestFormData?.vendor_email,
    });

    swageazyInventoryStore
      .createSwageazyInventoryPurchaseRequest()
      .then(() => {
        showSuccess("Purchase Request Created");
        swageazyInventoryStore.resetCreatePurchaseRequestState();
        swageazyInventoryStore.managePopup("openCreatePrPopup", "close");
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  const handleInwardTransfer = () => {
    swageazyInventoryStore
      .createSwageazyInventoryInwardTransfer()
      .then(() => {
        swageazyInventoryStore.setInwardRequestFormData({
          box_id: undefined,
        });
        showSuccess("Inward Transferred Successfully");
        swageazyInventoryStore.managePopup("openInwardPopup", "close");
        swageazyInventoryStore.getSwageazyInventoryListing(params);
      })
      .catch((err: any) => {
        swageazyInventoryStore.setInwardRequestFormData({
          box_id: undefined,
        });
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong! Please try again later.",
        );
      });
  };

  const handleOutwardTransfer = () => {
    const formData = swageazyInventoryStore.outwardRequestFormData;

    if (!formData?.po_id) {
      showError("Validation Error", "Please select a valid PO Number.");
      return;
    }

    if (!formData?.warehouse_id || formData.warehouse_id === "-1") {
      showError("Validation Error", "Please select a valid Warehouse.");
      return;
    }

    const quantityDetails = (formData?.quantity_details || []).filter(
      (detail) => Number(detail.quantity) > 0,
    );

    if (quantityDetails.length === 0) {
      showError(
        "Validation Error",
        "Please resolve boxes and keep at least one quantity before saving.",
      );
      return;
    }

    for (const detail of quantityDetails) {
      if (!detail.product_id || !detail.sku || !detail.box_id) {
        showError(
          "Validation Error",
          "Each debit line must include product, SKU, and box ID.",
        );
        return;
      }
      if (
        detail.max_quantity !== undefined &&
        Number(detail.quantity) > Number(detail.max_quantity)
      ) {
        showError(
          "Validation Error",
          `Quantity for SKU "${detail.sku}" in box "${detail.box_id}" cannot exceed available stock of ${detail.max_quantity}.`,
        );
        return;
      }
    }

    swageazyInventoryStore.setIsLoading(true);
    swageazyInventoryStore
      .createSwageazyInventoryOutwardTransfer()
      .then((submittedTotals) => {
        const currentUserName = userStore.user?.name || "System";

        submittedTotals.forEach((total) => {
          swageazyInventoryStore.updateInventoryListingAfterOutward(
            total.product_id,
            total.quantity,
            currentUserName,
          );
        });

        showSuccess("Outward Transfer Created");
        swageazyInventoryStore.managePopup("openOutwardPopup", "close");
        swageazyInventoryStore.getSwageazyInventoryListing(params);
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  const handleBoxTransfer = () => {
    if (
      swageazyInventoryStore.isLoading ||
      swageazyInventoryStore.isBoxTransferLoading
    ) {
      return;
    }

    const formData = swageazyInventoryStore.boxTransferFormData;

    if (!formData.warehouse_id) {
      showError("Validation Error", "Please select a warehouse.");
      return;
    }
    if (!formData.source_box_id?.trim()) {
      showError("Validation Error", "Please select a source box.");
      return;
    }
    if (!formData.dest_box_id?.trim()) {
      showError("Validation Error", "Please select a destination box.");
      return;
    }
    if (
      formData.source_box_id.trim().toUpperCase() ===
      formData.dest_box_id.trim().toUpperCase()
    ) {
      showError(
        "Validation Error",
        "Source and destination boxes must be different.",
      );
      return;
    }

    const selectedLines = (formData.stock_lines || []).filter(
      (line) => Boolean(line.selected) && Number(line.transfer_quantity) > 0,
    );
    if (selectedLines.length === 0) {
      showError(
        "Validation Error",
        "Select at least one SKU and enter a transfer quantity.",
      );
      return;
    }

    const invalidLine = selectedLines.find((line) => {
      const qty = Number(line.transfer_quantity);
      const available = Number(line.quantity);
      return !Number.isFinite(qty) || qty <= 0 || qty > available;
    });
    if (invalidLine) {
      showError(
        "Validation Error",
        `Transfer qty for SKU '${invalidLine.sku}' must be between 1 and ${invalidLine.quantity}.`,
      );
      return;
    }

    swageazyInventoryStore.setIsLoading(true);
    swageazyInventoryStore
      .createBoxTransfer()
      .then(() => {
        swageazyInventoryStore.managePopup("openBoxTransferPopup", "close");
        swageazyInventoryStore.resetBoxTransferFormData();
        swageazyInventoryStore.getSwageazyInventoryListing(params);
        const productId = swageazyInventoryStore.selectedProduct;
        if (
          productId &&
          swageazyInventoryStore.popupState.InventoryStockQuantityDetailsPopup
        ) {
          swageazyInventoryStore
            .getProductStockDetails(productId)
            .catch((refreshErr: any) => {
              showError(
                "Warning",
                refreshErr?.response?.data?.message ||
                  refreshErr?.message ||
                  "Transfer succeeded, but stock details failed to refresh. Reopen the product stock view.",
              );
            });
        }
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  const handleRackTransfer = () => {
    if (
      swageazyInventoryStore.isLoading ||
      swageazyInventoryStore.isRackTransferLoading
    ) {
      return;
    }
    const formData = swageazyInventoryStore.rackTransferFormData;
    if (!formData.warehouse_id) {
      showError("Validation Error", "Please select a warehouse.");
      return;
    }
    if (!formData.source_rack_id?.trim()) {
      showError("Validation Error", "Please select a source rack.");
      return;
    }
    if (!formData.dest_rack_id?.trim()) {
      showError("Validation Error", "Please select a destination rack.");
      return;
    }
    if (
      formData.source_rack_id.trim().toUpperCase() ===
      formData.dest_rack_id.trim().toUpperCase()
    ) {
      showError(
        "Validation Error",
        "Source and destination racks must be different.",
      );
      return;
    }
    const selectedBoxes = (formData.boxes || []).filter((box) => box.selected);
    if (selectedBoxes.length === 0) {
      showError("Validation Error", "Select at least one box to move.");
      return;
    }

    swageazyInventoryStore.setIsLoading(true);
    swageazyInventoryStore
      .createRackTransfer()
      .then(() => {
        swageazyInventoryStore.managePopup("openRackTransferPopup", "close");
        swageazyInventoryStore.resetRackTransferFormData();
        swageazyInventoryStore.getSwageazyInventoryListing(params);
        const productId = swageazyInventoryStore.selectedProduct;
        if (
          productId &&
          swageazyInventoryStore.popupState.InventoryStockQuantityDetailsPopup
        ) {
          swageazyInventoryStore
            .getProductStockDetails(productId)
            .catch((refreshErr: any) => {
              showError(
                "Warning",
                refreshErr?.response?.data?.message ||
                  refreshErr?.message ||
                  "Transfer succeeded, but stock details failed to refresh. Reopen the product stock view.",
              );
            });
        }
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  const handleSampleReturn = () => {
    const formData = swageazyInventoryStore.sampleReturnRequestFormData;

    if (!formData?.order_id) {
      showError("Validation Error", "Please select a valid Order ID.");
      return;
    }

    const sirOptions = swageazyInventoryStore.sampleReturnSirOptions;
    if (sirOptions.length > 0 && !formData?.stock_issue_request_id) {
      showError(
        "Validation Error",
        "Please select the stock issue request this return is for.",
      );
      return;
    }

    const skuItems = formData?.sku_items || [];
    if (
      skuItems.length === 0 ||
      !skuItems.some(
        (item) => Boolean(item.selected) && Number(item.quantity) > 0,
      )
    ) {
      showError(
        "Validation Error",
        "Select at least one SKU and enter a returned quantity.",
      );
      return;
    }

    for (const item of skuItems) {
      if (!item.selected || !Number(item.quantity)) continue;
      const pending = Number(item.pending_quantity ?? 0);
      if (pending > 0 && Number(item.quantity) > pending) {
        showError(
          "Validation Error",
          `Returned qty for SKU ${item.sku} cannot exceed pending qty (${pending}).`,
        );
        return;
      }
    }

    if (!formData?.box_id) {
      showError("Validation Error", "Please enter a valid Box ID.");
      return;
    }

    if (!formData?.current_rack) {
      showError("Validation Error", "Please select a valid Rack.");
      return;
    }

    swageazyInventoryStore.setIsLoading(true);
    swageazyInventoryStore
      .sampleReturn()
      .then((submittedTotals) => {
        const currentUserName = userStore.user?.name || "System";

        submittedTotals.forEach((total) => {
          swageazyInventoryStore.updateInventoryListingAfterSampleReturn(
            total.product_id,
            total.quantity,
            currentUserName,
          );
        });

        showSuccess("Sample Returned Successfully");
        swageazyInventoryStore.managePopup("openSampleReturnPopup", "close");
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            err?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  /** Refresh listing (and the open stock popup) after an adjustment or transfer. */
  const handleAdjustmentSaved = () => {
    swageazyInventoryStore
      .getSwageazyInventoryListing(params)
      .catch((err: any) => {
        showError(
          "Warning",
          err?.response?.data?.message ||
            "Saved, but the listing failed to refresh.",
        );
      });
    const productId = swageazyInventoryStore.selectedProduct;
    if (
      productId &&
      swageazyInventoryStore.popupState.InventoryStockQuantityDetailsPopup
    ) {
      swageazyInventoryStore.getProductStockDetails(productId).catch(() => {
        showError("Warning", "Saved, but stock details failed to refresh.");
      });
    }
  };

  const toAdjustmentTarget = (item: SwageazyInventoryDetails) => ({
    id: item.id,
    name: item.name,
  });

  /** Product currently open in the stock details popup. */
  const getSelectedAdjustmentTarget = () => {
    const productId = swageazyInventoryStore.selectedProduct;
    if (!productId) return null;
    const product =
      swageazyInventoryStore.swageazyInventoryListingResponse.find(
        (p) => p.id === productId,
      );
    return { id: productId, name: product?.name || "Product" };
  };

  const handleViewAuditLogsForSelected = () => {
    const target = getSelectedAdjustmentTarget();
    if (target) adjustmentStore.openAudit(target);
  };

  const handleViewTransactionsForSelected = () => {
    const target = getSelectedAdjustmentTarget();
    if (target) adjustmentStore.openTransactions(target);
  };

  const handleActionMenuOpen = () => {
    setActionsAnchorEl(actionsAnchorRef.current);
  };

  const handleActionMenuClose = () => {
    setActionsAnchorEl(null);
  };

  const handleDownloadData = () => {
    swageazyInventoryStore
      .downloadSwageazyInventoryReport(params)
      .then((response) => {
        if (response.data) {
          const downloadUrl = window.URL.createObjectURL(response.data);
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.download = response.filename || "swageazy-inventory.csv";
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        }
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => {
        handleActionMenuClose();
      });
  };

  const handleUpdateExistingSkuClick = () => {
    handleActionMenuClose();
    skuCsvInputRef.current?.click();
  };

  const handleDownloadSkuTemplate = () => {
    const a = document.createElement("a");
    a.href =
      "https://content.swageazy.com/templates/SwageazyInventory_SKU_Update_Template.csv";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = "SwageazyInventory_SKU_Update_Template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    handleActionMenuClose();
  };

  const handleSkuCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      showError("Invalid file", "Please upload a CSV file.");
      return;
    }

    swageazyInventoryStore.setIsLoading(true);
    swageazyInventoryStore
      .uploadInventoryVariantSkuCsv(file)
      .then((response: any) => {
        showSuccess(
          response?.message || "SKU update CSV uploaded successfully.",
        );
        swageazyInventoryStore.getSwageazyInventoryListing(params);
      })
      .catch((err: any) => {
        showError(
          "Error",
          err?.response?.data?.message ||
            "Failed to upload SKU CSV. Please try again later.",
        );
      })
      .finally(() => {
        swageazyInventoryStore.setIsLoading(false);
      });
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }} sx={{ px: 2 }}>
      <Backdrop
        sx={(theme) => ({
          color: theme.palette.customColors.white,
          zIndex: theme.zIndex.drawer + 999999,
        })}
        open={swageazyInventoryStore.isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Paper>
        {userStore.hasPermission(EmployeeRoles.VIEW_SWAGEAZY_INVENTORY) ? (
          <Box
            sx={{
              marginTop: "0.6rem",
              pt: 5,
              pl: 5,
              pr: 5,
              pb: 1,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: window.innerHeight - 110,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                alignItems: "center",
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <CustomTypography variant="heading3" fontWeight={700}>
                  Swageazy Inventory
                </CustomTypography>
              </Box>
              {(canEditInventory || canAdjustMoveInventory) && (
                <Box
                  sx={{
                    display: "flex",
                    marginLeft: "auto",
                    flexWrap: "wrap",
                  }}
                >
                  <input
                    ref={skuCsvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleSkuCsvUpload}
                    style={{ display: "none" }}
                  />
                  <Box ref={actionsAnchorRef}>
                    <AppButton
                      variant="grayscale"
                      label="Actions"
                      onClick={handleActionMenuOpen}
                      customStyles={{ minWidth: 180 }}
                    />
                  </Box>
                  <Menu
                    anchorEl={actionsAnchorEl}
                    open={Boolean(actionsAnchorEl)}
                    onClose={handleActionMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    disableAutoFocusItem
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow:
                            "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                          minWidth: 240,
                          overflow: "hidden",
                        },
                      },
                    }}
                  >
                    {canEditInventory && (
                      <MenuItem
                        onClick={() => {
                          handleActionMenuClose();
                          swageazyInventoryStore.managePopup(
                            "openCreatePrPopup",
                            "open",
                          );
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Create PR
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canEditInventory && (
                      <MenuItem
                        onClick={() => {
                          handleActionMenuClose();
                          swageazyInventoryStore.managePopup(
                            "openInwardPopup",
                            "open",
                          );
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Inward
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canEditInventory && (
                      <MenuItem
                        onClick={() => {
                          handleActionMenuClose();
                          swageazyInventoryStore.managePopup(
                            "openOutwardPopup",
                            "open",
                          );
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Outward
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canEditInventory && (
                      <MenuItem
                        onClick={() => {
                          handleActionMenuClose();
                          swageazyInventoryStore.managePopup(
                            "openSampleReturnPopup",
                            "open",
                          );
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Sample Return
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canAdjustMoveInventory && (
                      <MenuItem
                        onClick={() => {
                          handleActionMenuClose();
                          swageazyInventoryStore.openBoxTransferBlank();
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Box Transfer
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canEditInventory && (
                      <MenuItem
                        onClick={() => {
                          handleActionMenuClose();
                          swageazyInventoryStore.openRackTransferBlank();
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Rack Transfer
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canAdjustMoveInventory && (
                      <MenuItem
                        onClick={() => {
                          handleActionMenuClose();
                          downloadInventoryAdjustmentTemplate();
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Download Adjust Template
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canAdjustMoveInventory && (
                      <MenuItem
                        onClick={() => {
                          handleActionMenuClose();
                          adjustmentStore.openCsv();
                        }}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Upload Adjust Template
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canEditInventory && (
                      <MenuItem
                        onClick={handleDownloadSkuTemplate}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Download SKU Update Template
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canEditInventory && (
                      <MenuItem
                        onClick={handleUpdateExistingSkuClick}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Update Existing SKU
                        </CustomTypography>
                      </MenuItem>
                    )}
                    {canEditInventory && (
                      <MenuItem
                        onClick={handleDownloadData}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          gap: 1,
                          borderTop: "1px solid " + theme.palette.divider,
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightPurple ||
                              theme.palette.action.hover,
                          },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Download Data
                        </CustomTypography>
                      </MenuItem>
                    )}
                  </Menu>
                </Box>
              )}
            </Box>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: isSearchExpanded ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading6">Search</CustomTypography>
                <IconButton size="small">
                  {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
                <Grid2
                  container
                  spacing={2}
                  columns={12}
                  padding={2}
                  sx={{ alignItems: "flex-end" }}
                >
                  <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                    <CustomTextField
                      placeholder="Search by Product ID or Name"
                      value={searchTerm}
                      onKeyDown={handleKeyDown}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      width={"100%"}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <AppDropDown
                      title="Warehouse"
                      placeholderText="All Warehouses"
                      isMultiSelect={false}
                      value={draftParams.warehouse_id || ""}
                      list={[
                        { id: "", value: "All Warehouses" },
                        ...warehouseStore.warehouses.map((warehouse) => ({
                          id: String(warehouse.id),
                          value: warehouse.identifier,
                        })),
                      ]}
                      fontSize={"12px"}
                      onSingleSelectionChange={(id) => {
                        setDraftParams((prev) => ({
                          ...prev,
                          warehouse_id: id,
                        }));
                      }}
                      sx={{ width: "100%" }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                    <AppDropDown
                      title="Stock Status"
                      placeholderText="All Stock Statuses"
                      isMultiSelect={false}
                      value={draftParams.stock_status || ""}
                      list={SWAGEAZY_INVENTORY_STOCK_STATUS_OPTIONS.map(
                        (option) => ({
                          id: option.id,
                          value: option.value,
                        }),
                      )}
                      fontSize={"12px"}
                      onSingleSelectionChange={(id) => {
                        setDraftParams((prev) => ({
                          ...prev,
                          stock_status:
                            id as SwageazyInventoryFilters["stock_status"],
                        }));
                      }}
                      sx={{ width: "100%" }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      sx={{ mb: 0.75 }}
                    >
                      Available Qty
                    </CustomTypography>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                      }}
                    >
                      <CustomTextField
                        placeholder="Min"
                        type="number"
                        allowNegativeValue={false}
                        allowDecimalValue={false}
                        value={draftParams.available_qty_min ?? ""}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => {
                          setQtyFilterError("");
                          setDraftParams((prev) => ({
                            ...prev,
                            available_qty_min: parseQtyInput(e.target.value),
                          }));
                        }}
                        width="100%"
                      />
                      <CustomTextField
                        placeholder="Max"
                        type="number"
                        allowNegativeValue={false}
                        allowDecimalValue={false}
                        value={draftParams.available_qty_max ?? ""}
                        helperText={qtyFilterError}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => {
                          setQtyFilterError("");
                          setDraftParams((prev) => ({
                            ...prev,
                            available_qty_max: parseQtyInput(e.target.value),
                          }));
                        }}
                        width="100%"
                      />
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 2, lg: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        height: "100%",
                        alignItems: "flex-end",
                        pb: qtyFilterError ? 2.5 : 0,
                      }}
                    >
                      <AppButton
                        variant="transparentPrimary"
                        label="Clear"
                        onClick={resetFilters}
                        customStyles={{ width: "100%" }}
                      />
                      <AppButton
                        variant="primary"
                        label="Apply"
                        onClick={handleApply}
                        customStyles={{ width: "100%" }}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>

            {swageazyInventoryStore.isFetchingData ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: window.innerHeight - 340,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <SwageazyInventoryProductsTable
                handleViewImages={handleViewImagesOfProduct}
                maxHeight={window.innerHeight - 340}
                limit={params.limit || swageazyInventoryStore.limit}
                page_no={params.page_no || 1}
                total_count={swageazyInventoryStore.total_count}
                total_pages={swageazyInventoryStore.total_pages}
                sortBy={params.sort_by || "name"}
                sortType={params.sort_type as SortType}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
                data={swageazyInventoryStore.swageazyInventoryListingResponse}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                handleQuantityClick={(id: number) =>
                  handleViewInwardQuantityDetails(id)
                }
                canEdit={canAdjustMoveInventory}
                canTransferSku={canTransferSKU}
                onAdjust={(item) =>
                  adjustmentStore.openAdjust(toAdjustmentTarget(item))
                }
                onSkuTransfer={(item) =>
                  adjustmentStore.openTransfer(toAdjustmentTarget(item))
                }
                onViewAuditLogs={(item) =>
                  adjustmentStore.openAudit(toAdjustmentTarget(item))
                }
                onViewTransactions={(item) =>
                  adjustmentStore.openTransactions(toAdjustmentTarget(item))
                }
              />
            )}
          </Box>
        ) : (
          <PermissionBlockLabel />
        )}
      </Paper>
      <ImageGalleryPopup
        key={"productImages"}
        open={swageazyInventoryStore.popupState.openImageGallery}
        onClose={() =>
          swageazyInventoryStore.managePopup("openImageGallery", "close")
        }
        images={
          swageazyInventoryStore.selectedProductImages?.length > 0
            ? swageazyInventoryStore.selectedProductImages?.map((img) => ({
                id: swageazyInventoryStore.selectedProduct || 0,
                src: img,
              })) || []
            : []
        }
        title={"Product Images"}
      />

      <CreatePurchaseRequestPopup
        open={swageazyInventoryStore.popupState.openCreatePrPopup}
        orderId={-1}
        onClose={() =>
          swageazyInventoryStore.managePopup("openCreatePrPopup", "close")
        }
        showSummary={true}
        showGmailRecipients={true}
        enableProductSearch={true}
        handlePr={handlePr}
      />

      <SwageazyInventoryInwardPopup
        open={swageazyInventoryStore.popupState.openInwardPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openInwardPopup", "close")
        }
        onSave={handleInwardTransfer}
      />
      <SwageazyInventoryOutwardPopup
        open={swageazyInventoryStore.popupState.openOutwardPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openOutwardPopup", "close")
        }
        onSave={handleOutwardTransfer}
      />

      <SwageazyInventorySampleReturn
        open={swageazyInventoryStore.popupState.openSampleReturnPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openSampleReturnPopup", "close")
        }
        onSave={handleSampleReturn}
      />

      <SwageazyInventoryBoxTransferPopup
        open={swageazyInventoryStore.popupState.openBoxTransferPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openBoxTransferPopup", "close")
        }
        onSave={handleBoxTransfer}
      />

      <SwageazyInventoryRackTransferPopup
        open={swageazyInventoryStore.popupState.openRackTransferPopup}
        onClose={() =>
          swageazyInventoryStore.managePopup("openRackTransferPopup", "close")
        }
        onSave={handleRackTransfer}
      />

      <SwageazyInventoryStockQuantityDetailsPopup
        open={
          swageazyInventoryStore.popupState.InventoryStockQuantityDetailsPopup
        }
        onClose={() =>
          swageazyInventoryStore.managePopup(
            "InventoryStockQuantityDetailsPopup",
            "close",
          )
        }
        onViewAuditLogs={handleViewAuditLogsForSelected}
        onViewTransactions={handleViewTransactionsForSelected}
      />

      <SwageazyInventoryAdjustPopup
        store={adjustmentStore}
        onSaved={handleAdjustmentSaved}
      />
      <SwageazyInventorySkuTransferPopup
        store={adjustmentStore}
        onSaved={handleAdjustmentSaved}
      />
      <SwageazyInventoryAdjustCsvPopup
        store={adjustmentStore}
        onSaved={handleAdjustmentSaved}
      />
      <SwageazyInventoryAuditLogDrawer store={adjustmentStore} />
      <SwageazyInventoryTransactionsDrawer store={adjustmentStore} />
    </Grid2>
  );
};
export default observer(SwageazyInventoryListing);
